import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function ContactCTA() {
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation({ threshold: 0.1 });

  const [formData, setFormData] = useState({
    companyName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('companyName', formData.companyName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6fach1ghdq4qda6vjm0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ companyName: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact-section"
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/123a0062e0685b140d5812bfd535d597.jpg"
          alt="Australian landscape background"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark overlay for contrast */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/60 to-black/50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* Left Column */}
          <div
            ref={leftRef as React.RefObject<HTMLDivElement>}
            className={`transition-all duration-700 ease-out ${
              leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Ready to build your team's capability?
            </h2>
            <p className="text-lg sm:text-xl text-[#E8C580] mb-8">
              Get in touch for company pricing and course details
            </p>
            <div className="mb-6">
              <a
                href="mailto:info@indigenousfacilitation.com.au"
                className="text-lg sm:text-2xl text-[#E48523] hover:text-[#E8C580] transition-colors font-medium break-all sm:break-normal"
              >
                info@indigenousfacilitation.com.au
              </a>
            </div>
            <p className="text-sm text-[#E8C580] opacity-80">
              We'll respond within 1 business day
            </p>
          </div>

          {/* Right Column - Form */}
          <div
            ref={rightRef as React.RefObject<HTMLDivElement>}
            className={`bg-white/95 backdrop-blur-sm rounded-2xl p-5 sm:p-8 lg:p-10 shadow-2xl transition-all duration-700 ease-out delay-150 ${
              rightVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <form onSubmit={handleSubmit} data-readdy-form id="course-enquiry-form">
              <div className="space-y-5">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-semibold text-[#0E0503] mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[#FFF9F5] border border-[#E8C580] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#BA3D03] text-[#0E0503] text-sm"
                    placeholder="Your organisation"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#0E0503] mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[#FFF9F5] border border-[#E8C580] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#BA3D03] text-[#0E0503] text-sm"
                    placeholder="your.email@company.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-[#0E0503] mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[#FFF9F5] border border-[#E8C580] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#BA3D03] text-[#0E0503] text-sm"
                    placeholder="04XX XXX XXX"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-[#0E0503] mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 bg-[#FFF9F5] border border-[#E8C580] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#BA3D03] text-[#0E0503] resize-none text-sm"
                    placeholder="Tell us about your team and training needs..."
                  ></textarea>
                  <p className="text-xs text-[#3D1203] opacity-60 mt-1">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-[#BA3D03] text-white rounded-lg font-bold text-base sm:text-lg hover:bg-[#3D1203] transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Send Enquiry'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 text-sm font-medium">
                      Thank you! We'll be in touch within 1 business day.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 text-sm font-medium">
                      Something went wrong. Please try again or{' '}
                      <a
                        href="mailto:info@indigenousfacilitation.com.au"
                        className="underline hover:text-red-600 transition-colors"
                      >
                        email us directly
                      </a>
                      .
                    </p>
                  </div>
                )}
              </div>
            </form>
          </div>

        </div>
      </div>
    </section>
  );
}
